import React, { createContext, useState, useEffect, useCallback, ReactNode } from 'react'
import { auth, signInWithToken } from '@/app/config/firebase'
import { User as FirebaseUser } from 'firebase/auth'
import { USER_PROFILE_STORAGE_KEY, AUTH_ENDPOINT } from '@/app/config/settings'

interface User {
  name?: string
  email?: string
  picture?: string
  firebaseUser?: FirebaseUser
}

interface AuthContextType {
  user: User | null
  isAuthenticated: boolean
  login: () => void
  logout: () => void
  loading: boolean
  getFirebaseToken: () => Promise<string | null>
  clearEventsState: () => void
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  const persistUserData = useCallback((userData: User) => {
    try {
      sessionStorage.setItem(
        USER_PROFILE_STORAGE_KEY,
        JSON.stringify({
          name: userData.name,
          email: userData.email,
          picture: userData.picture,
        }),
      )
    } catch (error) {
      console.error('Failed to persist user data:', error)
    }
  }, [])

  const retrieveUserData = (): User | null => {
    try {
      const storedData = sessionStorage.getItem(USER_PROFILE_STORAGE_KEY)
      if (!storedData) return null

      return JSON.parse(storedData) as User
    } catch (error) {
      console.error('Failed to retrieve user data:', error)
      return null
    }
  }

  const getFirebaseToken = useCallback(async (): Promise<string | null> => {
    if (auth.currentUser) {
      try {
        return await auth.currentUser.getIdToken()
      } catch (error) {
        console.error('Failed to get Firebase ID token:', error)
        return null
      }
    }
    return null
  }, [])

  const fetchUserWithToken = useCallback(
    async (token: string) => {
      try {
        console.log('Attempting to authenticate with Firebase using token')
        const firebaseUserCredential = await signInWithToken(token)
        const firebaseUser = firebaseUserCredential.user

        const userData: User = {
          name: firebaseUser.displayName || undefined,
          email: firebaseUser.email || undefined,
          picture: firebaseUser.photoURL || undefined,
          firebaseUser: firebaseUser,
        }

        setUser(userData)
        persistUserData(userData)
      } catch (error) {
        console.error('Error authenticating with token:', error)
      } finally {
        setLoading(false)
      }
    },
    [persistUserData],
  )

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const token = urlParams.get('token')

    if (token) {
      window.history.replaceState({}, document.title, window.location.pathname)
      fetchUserWithToken(token)
    }
  }, [fetchUserWithToken])

  useEffect(() => {
    // Check if Firebase already has a user (persisted session)
    const unsubscribe = auth.onAuthStateChanged(async (firebaseUser) => {
      if (firebaseUser) {
        // User is signed in
        const userData: User = {
          name: firebaseUser.displayName || undefined,
          email: firebaseUser.email || undefined,
          picture: firebaseUser.photoURL || undefined,
          firebaseUser: firebaseUser,
        }

        setUser(userData)
        persistUserData(userData)
      } else {
        // User is signed out
        setUser(null)
        try {
          sessionStorage.removeItem(USER_PROFILE_STORAGE_KEY)
        } catch (error) {
          console.error('Error clearing auth state:', error)
        }
      }
      setLoading(false)
    })

    // Handle storage events (for multi-tab support)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === USER_PROFILE_STORAGE_KEY) {
        if (e.newValue === null) {
          // Another tab logged out
          setUser(null)
        } else {
          // Another tab logged in
          const userData = retrieveUserData()
          if (userData) {
            setUser(userData)
          }
        }
      }
    }

    window.addEventListener('storage', handleStorageChange)

    return () => {
      unsubscribe()
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [persistUserData])

  const login = () => {
    const returnUrl = encodeURIComponent(window.location.href.replace(/\/+$/, ''))
    window.location.href = `${AUTH_ENDPOINT}?return_to=${returnUrl}`
  }

  const clearEventsState = () => {
    try {
      // Dispatch a custom event to notify components to clear their state
      const event = new CustomEvent('clearEventsState')
      window.dispatchEvent(event)

      console.log('Events state cleared from memory')
    } catch (error) {
      console.error('Error clearing events state:', error)
    }
  }

  const logout = () => {
    auth
      .signOut()
      .then(() => {
        setUser(null)
        try {
          sessionStorage.removeItem(USER_PROFILE_STORAGE_KEY)
          // Clear events state from memory
          clearEventsState()
        } catch (error) {
          console.error('Error clearing auth state:', error)
        }
      })
      .catch((error) => {
        console.error('Error signing out from Firebase:', error)
      })
  }

  const value = {
    user,
    isAuthenticated: !!user,
    login,
    logout,
    loading,
    getFirebaseToken,
    clearEventsState,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
